import { Routes, Route, Navigate } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { useStore } from "@/stores";
import { HomePage } from "@/pages/HomePage";
import { OverviewPage } from "@/pages/OverviewPage";
import { LoginPage } from "@/pages/LoginPage";
import { ProfilePage } from "@/pages/ProfilePage";
import { SettingsPage } from "@/pages/SettingsPage";
import { CreateEditListingPage } from "@/pages/CreateEditListingPage";
import { MyListingsPage } from "@/pages/MyListingsPage";
import { ListingDetailPage } from "@/pages/ListingDetailPage";
import { VerificationRequestsPage } from "@/pages/VerificationRequestsPage";
import VerifyEmailPage from "@/pages/VerifyEmailPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import { TermsOfUsePage } from "@/pages/legal/TermsOfUsePage";
import { PartnerTermsPage } from "@/pages/legal/PartnerTermsPage";
import { PrivacyPolicyPage } from "@/pages/legal/PrivacyPolicyPage";
import { TokushoHoPage } from "@/pages/legal/TokushoHoPage";
import { LoadingScreen, GlobalSnackbar } from "@/shared/ui/components";
import { ProtectedRoute } from "@/features/auth/components/ProtectedRoute";
import { AppRoutes as AppRoutesPaths } from "@/shared/constants";
import { StoreProvider } from "@/stores";

const AppRoutes = observer(function AppRoutes() {
  const { authStore } = useStore();
  const { user, loading } = authStore;

  if (loading) {
    return <LoadingScreen />;
  }

  return (
    <Routes>
      {/* Public home page */}
      <Route path={AppRoutesPaths.home} element={<HomePage />} />

      {/* Public overview/search results page */}
      <Route path={AppRoutesPaths.overview} element={<OverviewPage />} />

      {/* Login page - redirect to profile if already logged in */}
      <Route
        path={AppRoutesPaths.login}
        element={
          user ? (
            <Navigate to={AppRoutesPaths.profile} replace />
          ) : (
            <LoginPage />
          )
        }
      />

      {/* Redirect legacy dashboard to profile */}
      <Route
        path={AppRoutesPaths.dashboard}
        element={<Navigate to={AppRoutesPaths.profile} replace />}
      />

      {/* Protected profile page */}
      <Route
        path={AppRoutesPaths.profile}
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />

      {/* Protected settings page */}
      <Route
        path={AppRoutesPaths.settings}
        element={
          <ProtectedRoute>
            <SettingsPage />
          </ProtectedRoute>
        }
      />

      {/* Protected create listing page (partners only) */}
      <Route
        path={AppRoutesPaths.createListing}
        element={
          <ProtectedRoute>
            <CreateEditListingPage />
          </ProtectedRoute>
        }
      />

      {/* Protected edit listing page (partners only) */}
      <Route
        path="/listings/:id/edit"
        element={
          <ProtectedRoute>
            <CreateEditListingPage />
          </ProtectedRoute>
        }
      />

      {/* Protected my listings page (partners only) */}
      <Route
        path={AppRoutesPaths.myListings}
        element={
          <ProtectedRoute>
            <MyListingsPage />
          </ProtectedRoute>
        }
      />

      {/* Public listing detail page */}
      <Route path="/listings/:id" element={<ListingDetailPage />} />

      {/* Protected verification requests page (admin/developer only) */}
      <Route
        path={AppRoutesPaths.verificationRequests}
        element={
          <ProtectedRoute>
            <VerificationRequestsPage />
          </ProtectedRoute>
        }
      />

      {/* Email verification */}
      <Route path={AppRoutesPaths.verifyEmail} element={<VerifyEmailPage />} />

      {/* Password reset */}
      <Route
        path={AppRoutesPaths.resetPassword}
        element={<ResetPasswordPage />}
      />

      {/* Legal pages - public */}
      <Route path={AppRoutesPaths.legal.terms} element={<TermsOfUsePage />} />
      <Route
        path={AppRoutesPaths.legal.partnerTerms}
        element={<PartnerTermsPage />}
      />
      <Route
        path={AppRoutesPaths.legal.privacy}
        element={<PrivacyPolicyPage />}
      />
      <Route path={AppRoutesPaths.legal.tokusho} element={<TokushoHoPage />} />

      {/* Catch-all redirect to home */}
      <Route path="*" element={<Navigate to={AppRoutesPaths.home} replace />} />
    </Routes>
  );
});

export default function App() {
  return (
    <StoreProvider>
      <AppRoutes />
      <GlobalSnackbar />
    </StoreProvider>
  );
}
